import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";
import { sfx } from "@/lib/sounds";
import { Mail, KeyRound, User, Sparkles, Shield } from "lucide-react";

export default function AuthModal({ open, onOpenChange, initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const { login, register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [lostdrkcEnabled, setLostdrkcEnabled] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (!open) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/lostdrkc/status`)
      .then((r) => r.json())
      .then((d) => setLostdrkcEnabled(!!d?.enabled))
      .catch(() => setLostdrkcEnabled(false));
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        if (!form.name || form.name.length < 2) throw new Error("Name is required");
        await register({ name: form.name.trim(), email: form.email.trim().toLowerCase(), password: form.password });
        sfx.levelup();
        toast.success("Welcome to LostDC!", { description: "Account created" });
      } else {
        await login(form.email.trim().toLowerCase(), form.password);
        sfx.pling();
        toast.success("Logged in");
      }
      onOpenChange(false);
      setForm({ name: "", email: "", password: "" });
    } catch (err) {
      sfx.error();
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  const discordLogin = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/discord/login`);
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Discord not ready");
      }
      const { authorize_url } = await r.json();
      window.location.href = authorize_url;
    } catch (err) {
      sfx.error();
      toast.error(err.message || "Discord login unavailable — placeholder credentials in .env");
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const lostdrkcLogin = () => {
    sfx.pling();
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/lostdrkc/start`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black/95 border border-white/10 text-white sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={16} className="text-[#FFBF00]" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">LostDC Network</span>
          </div>
          <DialogTitle className="font-hero text-3xl text-white">
            {mode === "register" ? "Create Account" : "Welcome Back"}
          </DialogTitle>
          <DialogDescription className="text-white/50 text-xs">
            {mode === "register"
              ? "Join the LostDC Network — sync your Minecraft profile, follow creators and enter events."
              : "Sign in with email, Discord, Google or LostDRKC ID to continue."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          {mode === "register" && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Display Name</span>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                <Input data-testid="auth-name" value={form.name} onChange={set("name")} className="pl-9 bg-black/60 border-white/10 text-white" />
              </div>
            </label>
          )}
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Email</span>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
              <Input data-testid="auth-email" type="email" value={form.email} onChange={set("email")} className="pl-9 bg-black/60 border-white/10 text-white" />
            </div>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">Password</span>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
              <Input data-testid="auth-password" type="password" value={form.password} onChange={set("password")} className="pl-9 bg-black/60 border-white/10 text-white" />
            </div>
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit"
            className="w-full px-4 py-3 rounded-full bg-[#FFBF00] text-black font-semibold uppercase tracking-wider text-sm hover:bg-[#FF8C00] transition-colors disabled:opacity-60"
          >
            {busy ? "Working..." : mode === "register" ? "Create Account" : "Sign In"}
          </button>
        </form>

        <div className="relative my-3">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
          <div className="relative flex justify-center"><span className="bg-black px-2 text-[10px] uppercase tracking-widest text-white/40">Or</span></div>
        </div>

        <button
          type="button"
          onClick={googleLogin}
          data-testid="auth-google"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-white hover:bg-white/90 text-black text-sm font-semibold uppercase tracking-wider transition-colors mb-2"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>
        {lostdrkcEnabled && (
          <button
            type="button"
            onClick={lostdrkcLogin}
            data-testid="auth-lostdrkc"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-sm font-bold uppercase tracking-wider mb-2 border-2 border-black blocky-shadow-black hover-blocky transition-transform"
          >
            <Shield size={16} /> Sign in with LostDRKC ID
          </button>
        )}
        <button
          type="button"
          onClick={discordLogin}
          data-testid="auth-discord"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#5865F2] hover:bg-[#4752C4] text-white text-sm font-semibold uppercase tracking-wider transition-colors"
        >
          Continue with Discord
        </button>

        <div className="text-center text-sm text-white/60 mt-3">
          {mode === "register" ? (
            <>Already have an account?{" "}
              <button type="button" data-testid="auth-switch-login" className="text-[#FFBF00] hover:underline" onClick={() => setMode("login")}>Sign in</button>
            </>
          ) : (
            <>New here?{" "}
              <button type="button" data-testid="auth-switch-register" className="text-[#FFBF00] hover:underline" onClick={() => setMode("register")}>Create one</button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
